import { useState, useEffect, useRef, useMemo } from 'react';
import { Image } from '../../types';
import {
  processImageMetadata,
  ImageURLDetailsByTimestamp,
} from '../utils/imageMetadataUtils';

export interface UseImageMetadataResult {
  imageURLDetailsByTimestamp: ImageURLDetailsByTimestamp;
}

// Create a stable key from image URLs to detect actual content changes
const getImagesKey = (images: Image[]): string => {
  return images.map(img => img.url).sort().join('|');
};

export const useImageMetadata = (
  images: Image[],
  loading: boolean,
  terminalOffset: string | undefined,
  proNumber: string | undefined
): UseImageMetadataResult => {
  const [imageURLDetailsByTimestamp, setImageURLDetailsByTimestamp] =
    useState<ImageURLDetailsByTimestamp>({});
  
  const processedImagesKey = useRef<string | null>(null);
  const processedProNumber = useRef<string | null>(null);
  const isProcessing = useRef<boolean>(false);

  // Create a stable key from image URLs
  const imagesKey = useMemo(() => getImagesKey(images), [images]);

  useEffect(() => {
    const processMetadata = async () => {
      // Skip if already processing, same proNumber, or same images
      if (
        isProcessing.current || 
        processedProNumber.current === proNumber ||
        processedImagesKey.current === imagesKey
      ) {
        return;
      }

      if (!images.length || !terminalOffset) {
        return;
      }

      try {
        isProcessing.current = true;

        const metadata = await processImageMetadata(
          images,
          terminalOffset
        );
        setImageURLDetailsByTimestamp(metadata);
        processedProNumber.current = proNumber || null;
        processedImagesKey.current = imagesKey;
      } catch (err) {
        console.error('Error processing image metadata:', err);
      } finally {
        isProcessing.current = false;
      }
    };

    if (!loading && images.length > 0) {
      processMetadata();
    }
  }, [imagesKey, loading, terminalOffset, proNumber, images]);

  useEffect(() => {
    // Reset when proNumber changes
    processedProNumber.current = null;
    processedImagesKey.current = null;
    setImageURLDetailsByTimestamp({});
  }, [proNumber]);

  return {
    imageURLDetailsByTimestamp,
  };
};
